import { useHorizontalScroll, useKeepInView, mergeRefs } from '@deltasierra/react-hooks';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import AddCircle from '@material-ui/icons/AddCircle';
import * as React from 'react';
import styled from 'styled-components';
import { DSGrid, DSPaper } from '../mui';
import { DSIconButton } from '../Generic/Buttons/DSIconButton';
import { MultiItemPickerImage } from './MultiItemPickerImage';
import { MultiItemPickerNavigator } from './MultiItemPickerNavigator';

const DnDProviderFixed = DndProvider as any;

const MultiItemPickerContainer = styled(DSPaper)`
    padding: 8px;
    padding-bottom: 0px;
`;

const ImagesContainer = styled.ul`
    display: flex;
    align-items: center;
    width: 100%;
    overflow-x: auto;
    padding: 0;
`;

const StyledIcon = styled(DSIconButton)`
    font-size: 48px;
    margin: 24px;
`;

export type MultiItemPickerProps<T extends { id: number | string; src?: string; title: string }> = {
    /**
     * The list of MultiItemPicker.items
     */
    items: T[];
    /**
     * The index of the currently selected image
     */
    value: number;
    /**
     * Whether to hide the navigator component in the picker
     */
    disableNavigator?: boolean;
    /**
     * Size of thumbnails in the picker
     */
    thumbnailSize?: number;
    /**
     * Notifier component to render
     */
    renderNotifier?: React.ReactChild;
    /**
     * When the component requests a change of value
     */
    onChange: (value: number) => void;
    /**
     * When a drop happens in a drag-and-drop
     */
    onDrop?: (from: number | string, to: number | string) => void;
} & ({
    /**
     * When the add new item icon is clicked
     */
    onAddNew?: () => void;
    /**
     * Whether a new item can be added using the picker
     */
    disableAdd?: boolean;
} | { onAddNew?: undefined; disableAdd?: undefined })
& (
    | {
        /**
         * When the close button is clicked
         */
        onRemove?: (id: number | string) => void;
        /**
         * Whether the last item in the picker can be removed
         */
        disableRemove?: boolean;
    }
    | { onRemove?: undefined; disableRemove?: undefined; }
);

export function MultiItemPicker<T extends { id: number | string; src?: string; title: string }>({
    disableAdd,
    disableNavigator,
    disableRemove,
    items,
    onAddNew,
    onChange,
    onDrop,
    onRemove,
    renderNotifier,
    thumbnailSize = 125,
    value = 0,
}: MultiItemPickerProps<T>): JSX.Element {
    const viewRef = useKeepInView<HTMLUListElement>(value);
    const scrollRef = useHorizontalScroll<HTMLUListElement>();
    const mergedRefs = mergeRefs([viewRef, scrollRef]);

    return (
        <MultiItemPickerContainer>
            <DnDProviderFixed backend={HTML5Backend}>
                <ImagesContainer ref={mergedRefs}>
                    {items.map(({ id, src, title }, index) => (
                        <MultiItemPickerImage
                            height={thumbnailSize}
                            id={id}
                            key={id}
                            selected={index === value}
                            src={src}
                            title={title}
                            width={thumbnailSize}
                            onClick={() => onChange(index)}
                            onClose={!disableRemove ? onRemove : undefined}
                            onDrop={onDrop}
                        />
                    ))}
                    {!disableAdd && (
                        <StyledIcon
                            color="primary"
                            data-cy="add-multi-item-button"
                            data-testid="add-multi-item-button"
                            onClick={() => onAddNew?.()}
                        >
                            <AddCircle fontSize="small" />
                        </StyledIcon>
                    )}
                </ImagesContainer>
            </DnDProviderFixed>
            <DSGrid alignItems="center" container justifyContent="space-between" wrap="nowrap">
                {!disableNavigator && (
                    <MultiItemPickerNavigator
                        isItem
                        max={items.length}
                        min={1}
                        value={value + 1}
                        onDecrement={() => onChange(value - 1)}
                        onIncrement={() => onChange(value + 1)}
                    />
                )}

                {renderNotifier && (
                    <DSGrid alignItems="center" container item justifyContent="flex-end">
                        {renderNotifier}
                    </DSGrid>
                )}
            </DSGrid>
        </MultiItemPickerContainer>
    );
}
MultiItemPicker.displayName = 'MultiItemPicker';
